import _ from 'lodash';

const TEXTS = {
  en: {
    sample: {
      title: 'Hello {{name}}',
    },
    menu: {
      solutions: 'Solutions',
      agencies: { title: 'Agencies', subtitle: 'Deliver more for every client' },
      publishers: { title: 'Publishers & media', subtitle: 'Engage audiences and own the data' },
      insurance: { title: 'Insurance', subtitle: 'Guide cover, claims and renewal journeys' },
      banking: { title: 'Banking', subtitle: 'Make complex product choices clearer' },
      payments: { title: 'Payments & cards', subtitle: 'Improve choice, benefits and onboarding' },
      healthcare: { title: 'Healthcare', subtitle: 'Navigate services and approved information' },
      realEstate: { title: 'Real estate', subtitle: 'Qualify intent and match the right property' },
      retail: { title: 'Retail & ecommerce', subtitle: 'Guide shoppers to relevant products' },
      telecommunications: { title: 'Telecommunications', subtitle: 'Clarify plans, devices and service journeys' },
      product: 'Product',
      platform: 'Platform',
      editor: { title: 'Professional studio', subtitle: 'Pixel-perfect control for designers' },
      aiCreation: { title: 'Governed AI creation', subtitle: 'Create fast inside approved rules' },
      aiEngine: { title: 'Engagement AI Engine', subtitle: 'Create and improve engagement products' },
      flow: { title: 'Smart flows', subtitle: 'Personalize every user journey' },
      cardsFormat: { title: 'Interactive formats', subtitle: 'Build experiences users act on' },
      infographics: { title: 'Storycards Infographics', subtitle: 'AI charts, maps and data stories' },
      brandGovernance: { title: 'Brand governance', subtitle: 'Keep every team on brand' },
      collaboration: { title: 'Teams & approvals', subtitle: 'Control roles, reviews and publishing' },
      dataAndAnalytics: 'Data & Analytics',
      dataAndOptimization: 'Data & Optimization',
      pricing: 'Pricing',
      about: 'Our story',
      analytics: { title: 'Analytics & optimization', subtitle: 'Find drop-offs and improve performance' },
      dpa: 'Data Processing Addendum',
      tos: 'Terms of Service',
      dataCollection: { title: 'Data collection', subtitle: 'Structured first-party data you own' },
      segments: { title: 'Audience segments', subtitle: 'Personalize every next step' },
      careers: 'Careers',
      pressCenter: 'Press Center',
      privacy: 'Privacy Policy',
      inspiration: 'Get Inspired',
      ai: 'AI engine',
      hosting: { title: 'Enterprise hosting', subtitle: 'Reliable infrastructure for high traffic' },
      security: { title: 'Security', subtitle: 'Controls built for enterprise standards' },
      integrations: { title: 'Integrations', subtitle: 'Connect experiences to your existing stack' },
      api: { title: 'API & webhooks', subtitle: 'Move data and content in real time' },
    },
    mainPage: {
      title: 'The no-code platform for engaging data collection enhanced by AI',
      titleMobile: 'The no-code platform for engaging data collection enhanced by AI',
    },
    header: {
      startBtnLabel: 'Start Creating',
      loginBtnLabel: 'Log In',
      startBtnLabelMobile: 'Start',
    },
    SCEditor: {
      title: 'Let’s meet Storycards Editor',
    },
    segments: {
      topSection: {
        title: 'Build audiences from what people actually choose',
        subTitle: 'Create segments from answers and actions inside your Storycards products.',
      },
      subTitle:
        'Create highly targeted audience segments based on user choices and launch advertising campaigns that truly resonate.',
      subTitleDescription: `Storycards allows you to embed marketing pixel code on any element, answer, or card. This pixel will activate only when a user selects a specific answer or reaches a particular card based on their choices.\n\nWhile there are many marketing tools available, the ability to create precise and focused audience segments makes Storycards the leading system for generating targeted audiences.\n\nYou can create a poll, trivia game, or simply ask your users a question, then segment them as an audience based on their choices. Use these segments to target ads in your app or website, tailored to the decisions users make within the story.`,
      infoSection: [
        {
          title: 'Smart audience segmentation',
          text: 'Create audience segments from the choices, actions and preferences people share inside a Storycards product.',
        },
        {
          title: 'Dynamic pixel activation',
          text: 'Embed marketing pixels on specific elements and trigger them based on user interactions for precise tracking.',
        },
        {
          title: 'Behavior-based targeting',
          text: 'Segment your audience by analyzing how users interact with polls, trivia, and questions in real-time.',
        },
        {
          title: 'Personalized ad campaigns',
          text: 'Use the resulting segments to run campaigns based on the decisions people made in the experience.',
        },
        {
          title: 'Customizable triggers',
          text: 'Set up custom triggers for segments using specific answers, actions, or cards, ensuring highly targeted data collection.',
        },
        {
          title: 'Send segments to your existing tools',
          text: 'Export segmented user data to your preferred CRM or third-party tools for further analysis and use.',
        },
        {
          title: 'In-story decision tracking',
          text: 'Track decisions at each step and use those signals to define meaningful audience groups.',
        },
        {
          title: 'Enhanced engagement analytics',
          text: 'Gain detailed insights into user behavior and engagement, optimizing future segmentation strategies.',
        },
        {
          title: 'Multi-platform audience sync',
          text: 'Synchronize your audience segments across platforms, ensuring consistent targeting and messaging.',
        },
        {
          title: 'Real-time segment updates',
          text: 'Automatically update audience segments as users interact with your stories for instant campaign optimization.',
        },
      ],
    },
    cardsFormat: {
      title: 'Engage your audience with a variety of content experiences that people love',
      heroInfo: {
        title:
          'Mix and match from a variety of our products such as quiz, poll, yes/no, Leads Form, and more to create a unique engagement experience.',
        text: 'Start with an interesting opening screen, then create engagement with your audience using different engagement cards and even collect information about your users with a lead form. ',
      },
      heroInfo2: {
        title: 'Engagement Logic',
        items: [
          {
            title: 'Smart scoring system',
            text: 'You can define multiple correct answers or allocate different amounts of points for correct answers by cards, and then show your users different content and cards according to the points they gained.',
          },
          {
            title: 'Navigate by answers',
            text: 'Give a different score to each answer and lead users to different results. Then, show your users different content according to their answers\\results.',
          },
          {
            title: 'Define repetition',
            text: 'Each product can be defined as a one-time per user activity, or as an unlimited participation activity.',
          },
          {
            title: 'Multiple flows',
            text: "Create Multiple flows according to the users' choices on each card. Mix Quiz with different types of cards.",
          },
          {
            title: 'Ask what you want',
            text: 'Add any fields to your form, and name it as you choose. Then define which fields are mandatory and add Form validations.',
          },
          {
            title: 'Share user result',
            text: 'Show the user his results at any stage you want and encourage him to share it.',
          },
        ],
      },
      cardTypes: {
        title: 'Mix and match from a variety of our products',
        register: {
          title: 'Registration',
          text: 'Start with an interesting opening screen, then create engagement with your audience using different engagement cards and even collect information about your users with a lead form.',
        },
        info: {
          title: 'Info',
          text: "Since you still want to tell a story amongst all the fun activities.\n\nYou can open your story with a motivational call for action and a CTA button, and also finish it by showing a specific text depending on the user's result. The information card is your way to show any content you want during the engagement.",
        },
        form: {
          title: 'Leads Form',
          text: 'Dramatically Increase the number of leads and get better quality information.\n\nA great way to increase your leads is to mix leads from inside the engaging products, the users feel comfortable sharing information while they play and are involved in your content. ',
        },
        personality: {
          title: 'Personality Quiz',
          text: "Go viral with a fun, engaging test that reveals something new about your audience.\n\nPeople always want to know more about themselves and how they are doing compared to others. Users love it. It's fun and a highly shareable format.",
        },
        trivia: {
          title: 'Trivia',
          text: "Check your users' knowledge and recommend further action based on how much they know.\n\nUsers love it. It's fun and highly shareable. Trivia improves your engagement and increases the time users spend on your site by more than 80%.",
        },
        poll: {
          title: 'Poll',
          text: 'People always have an opinion. Ask the right questions, know more about your users and show the results.\n\nThe best way to involve your users with your content increases the time users spend on your site and know more about your users.',
        },
        thisOrThat: {
          title: 'This or That',
          text: 'People always have an opinion. Ask the right questions, know more about your users and show the results.\n\nThe best way to involve your users with your content increases the time users spend on your site and know more about your users.',
        },
        trueOrFalse: {
          title: 'True or False',
          text: 'Check the knowledge of your users with a great engaging swipe card.\n\nA Great engaging card type with drag and drop way.',
        },
      },
    },
    editorPage: {
      topSection: {
        title: `The professional studio built for designers.`,
        subtitle:
          'Start with AI, bring designs directly from Figma, use an approved template or begin with a blank canvas. Control every layout, breakpoint, component and interaction while keeping every experience responsive and fully on brand.',
      },
      videoSectionUnderText:
        '<p><b>AI speed. Designer control.</b> Create with a prompt, then perfect every detail in a professional drag-and-drop studio.</p>',
      designerStory: {
        eyebrow: 'Built for design-led organizations',
        title: 'Designers define the system. Every team creates within it.',
        text: 'Storycards gives design teams a professional canvas and a practical way to turn brand guidelines into working rules. Templates, components and protected editing keep products aligned when other teams create or update them.',
        pillars: [
          {
            title: 'AI speed. Pixel-perfect control.',
            text: 'Use AI to move faster, then refine every layout, breakpoint, state and interaction in a fully editable professional canvas.',
          },
          {
            title: 'Turn Figma intent into live behavior',
            text: 'Connect the design together with its comments. Storycards AI reads the instructions attached to elements and builds the behavior designers described.',
          },
          {
            title: 'Make the brand system operational',
            text: 'Define reusable components, approved templates and protected areas so every team can publish without drifting away from the brand.',
          },
        ],
      },
      featureSection: {
        eyebrow: 'Studio capabilities',
        title: 'Everything designers need. All the control enterprises require.',
        text: 'From pixel-perfect craft and responsive behavior to reusable systems, protected editing and controlled publishing. Explore the complete Studio toolkit.',
      },
      infoItems: {
        designItTitle: 'Design as you want ',
        designItText: `Absolute control and freedom to design just the way you want it. Start with a template or from scratch and build your custom design. Smooth drag and drop, no coding required.`,
        notMoreTitle: 'Not more of the same',
        notMoreText:
          'Build custom layouts on a responsive drag-and-drop canvas instead of forcing each idea into a fixed template.',
        designResponsiveTitle: 'Responsive design',
        designResponsiveText: `Design any element differently for Mobile, Tablet or Desktop and adjust layouts by platform and device with an easy preview feature.`,
        fontsTitle: 'Use your own fonts',
        fontsText: `You can choose from our fonts gallery or upload your own fonts to match your brand. Adjust everything from line height to letter spacing. `,
        contentTitle: 'Add your content',
        contentText: `Work directly with our editor to add your content, texts and images into your card’s design, so you can see how your content looks as you design it in real time.`,
        platformTitle: 'Tailor your content by platform',
        platformText: `Adjust or change parts of your text and content between the mobile, tablet and desktop platforms, so a long title in the desktop version can become an alternate shorter text in the mobile version.`,
        createReusableTitle: 'Create reusable components',
        createReusableText: `Turn repeated elements into shared components. Update the component once and apply the change wherever it is used.`,
        gridTitle: 'Grid layouts and boxes',
        gridText: `Build grid layouts and boxes to your design and align them as desired with the editor menu. You also always have the option to write your own CSS code directly into the editor and style every element to perfection.`,
        globalStickyTitle: 'Create global and sticky elements',
        globalStickyText: `Define your headers, footers or any other element as global elements that will automatically display on all your Storycards. Apply element as “pin to card”, so it stays floating during scroll.`,
        animationTitle: 'Animation',
        animationText: `Choose any element of your card that you want to animate, and customize it's duration and delay.`,
        shareTitle: 'Add share buttons',
        shareText: `Add, design and place share buttons on your story, through which your users will be able to share the product with their friends.`,
        seoTitle: 'SEO tools',
        seoText: `Add your meta data with SEO tools, enter your story title and description.`,
        userTitle: 'Define repetition',
        userText: `Each product can be defined as a one time per user activity, or as an activity with unlimited participation.`,
        ownCodeTitle: 'Add your own code',
        ownCodeText: `And if all that's not enough for you, add your custom code on every card.`,
        marketingTitle: 'Marketing pixels',
        marketingText:
          "Adding your marketing pixels gives you the ability to create a custom audience by users' choices. You can add pixel code on any element, answer or card.",
        revisionTitle: 'Revision history',
        revisionText:
          'Everything you do inside the editor and any changes by other users on your team are saved automatically, you can go back at any time and use revision history to publish old versions.',
      },
      scrollSection: [
        {
          title: 'AI chat, side by side',
          desc: 'Give design instructions in the chat, watch every change appear live in the Studio and keep the result fully editable.',
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/get_personal.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/get_personal.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/get_personal.jpg' },
        },
        {
          title: 'Figma integration',
          desc: 'Bring designs and component comments directly from Figma, then let Storycards AI translate visual intent and written instructions into live behavior.',
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/get_personal.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/get_personal.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/get_personal.jpg' },
        },
        {
          title: 'Precision at every breakpoint',
          desc: 'Control layout, styling and content independently across desktop, tablet and mobile, with responsive auto-layouts and an instant preview.',
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/get_personal.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/get_personal.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/get_personal.jpg' },
        },
        {
          title: 'Reusable design systems',
          desc: 'Create components once, reuse them across every card and update shared design elements globally.',
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/scoring.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/scoring.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/scoring.jpg' },
        },
        {
          title: 'Auto layouts, grids and boxes',
          desc: 'Set responsive rules and watch components adapt across breakpoints. Wrap elements in boxes that grow and shrink with their content.',
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/url_parameters.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/url_parameters.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/url_parameters.jpg' },
        },
        {
          title: 'Design personalized journeys',
          desc: `Design every state and path so the experience can adapt to each user's actions, choices and preferences.`,
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/flowcondtion.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/flowcondtion.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/flowcondtion.jpg' },
        },
        {
          title: 'Advanced motion and interactions',
          desc: `Build states, animate any element and add Lottie animations with triggers tied to user actions.`,
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/flowcondtion.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/flowcondtion.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/flowcondtion.jpg' },
        },
      ],
      infoSection: [
        {
          title: 'Figma Integration',
          text: 'Connect designs and element comments directly from Figma. Storycards AI reads the instructions and helps turn the intended behavior into a fully editable product.',
        },
        {
          title: 'AI Chat, Side by Side',
          text: 'Give design instructions in the chat and see every change appear live in the Studio, while every result remains fully editable.',
        },
        {
          title: 'Pixel-Perfect Design Control',
          text: 'Start from AI, a template or a blank canvas, then control every layout, style and interaction with a precision drag-and-drop canvas.',
        },
        {
          title: 'Precision at Every Breakpoint',
          text: 'Design any element differently for mobile, tablet or desktop. Adjust layout and content for each device with an instant preview.',
        },
        {
          title: 'Drag and Drop',
          text: 'Add and position elements freely by dragging and dropping them onto the canvas.',
        },
        {
          title: 'Auto Layouts, Grids, and Boxes',
          text: 'Set parameters and watch your components adapt across breakpoints. Wrap elements in boxes that expand or shrink dynamically based on content.',
        },
        {
          title: 'Use Your Own Fonts',
          text: 'Choose from our font gallery or upload your own fonts to match your brand. Adjust everything from line height to letter spacing.',
        },
        {
          title: 'Personalized Journeys',
          text: `Adapt the design and user experience dynamically based on each user's actions, choices, and preferences within the product.`,
        },
        {
          title: 'Integrated A/B Testing Tools',
          text: 'Easily set up A/B tests directly in the designer to measure and improve product performance.',
        },
        {
          title: 'Overlays',
          text: 'Create pop-ups, tooltips, or menus within cards.',
        },
        {
          title: 'Sliders',
          text: 'Create vertical or horizontal sliders for text, images, video and other content.',
        },
        {
          title: 'AI-Powered Image Creation',
          text: 'Generate custom images using AI prompts directly in the designer. Choose styles, adjust details, and instantly add visuals to your story.',
        },
        {
          title: 'Generative Video Creation',
          text: 'Use AI to create short, engaging videos based on a text prompt or script.',
        },
        {
          title: 'Approved AI-Ready Templates',
          text: 'Turn your design system into approved templates with defined AI parameters, so teams can create quickly without breaking brand standards.',
        },
        {
          title: 'Brand Rules and Libraries',
          text: 'Define approved typography, colors, components and interaction rules so every team creates inside the system established by design.',
        },
        {
          title: 'Protected Editing',
          text: 'Lock brand-critical layouts, styles and interactions while allowing content teams to update only the fields you approve.',
        },
        {
          title: 'Controlled Publishing',
          text: 'Manage who can create, edit, review and publish each experience with roles, permissions and approval workflows.',
        },
        {
          title: 'Animations',
          text: 'Bring your elements to life with Lottie animations and triggers tied to user actions, such as confetti for correct answers or fade-outs for incorrect ones.',
        },
        {
          title: 'Gradients',
          text: 'Enhance section backgrounds with custom linear, radial, conic, and fluid gradients.',
        },
        {
          title: 'Crop and Resize Images',
          text: 'Easily crop and resize images directly in the designer. Adjust dimensions to fit your layout perfectly, with intuitive editing tools.',
        },
        {
          title: 'Image Auto-Optimize',
          text: 'Automatically optimize images for faster loading times without compromising quality. Ideal for improving performance on all devices.',
        },
        {
          title: 'Image Serve Auto Format',
          text: `Deliver images in the best format (e.g., WebP, PNG, JPEG) automatically based on the user's browser and device capabilities.`,
        },
        {
          title: 'Deliver the Best Image Size by Device',
          text: 'Serve appropriately scaled images tailored for desktop, tablet, or mobile to reduce load times and enhance user experience.',
        },
        {
          title: 'Tailor Your Content by Platform',
          text: 'Adjust or change parts of your text and content for mobile, tablet, and desktop. For example, a long title on desktop can become a shorter alternative for mobile.',
        },
        {
          title: 'Reusable Design Systems',
          text: 'Turn elements into shared components and update them across cards and products from a single source.',
        },
        {
          title: 'Create Global and Sticky Elements',
          text: 'Add headers, footers, CTA buttons, and other sticky elements to enhance navigation.',
        },
        {
          title: 'Text Scaling',
          text: 'Scale text across screen sizes automatically or define your own range for each breakpoint.',
        },
        {
          title: 'Custom CSS',
          text: 'Add your own CSS for detailed customization and full design control.',
        },
        {
          title: 'Counters',
          text: 'Create counters that count up or down. Customize their design, animation, and number transitions.',
        },
        {
          title: 'Timer',
          text: 'Add a visual or numerical timer with customizable design. Trigger actions when the timer ends, such as moving to the next step.',
        },
        {
          title: 'Design Forms and Fields',
          text: 'Design fields and buttons for forms freely, down to the level of error messages and placeholders.',
        },
        {
          title: 'Answers Component',
          text: 'Design a versatile answers component that can display text, images, or any other content, with fully customizable styles and states.',
        },
        {
          title: 'State-Based Answers',
          text: 'Define states for answers, including correct, incorrect, pending selection, or disabled, with smooth visual transitions.',
        },
        {
          title: 'Interactive Feedback Messages',
          text: 'Automatically show feedback messages based on user interactions, with support for rich content like icons, images, and text.',
        },
        {
          title: 'Add Share Buttons',
          text: 'Design and place share buttons to enable users to share your product with their friends.',
        },
        {
          title: 'Revision History',
          text: 'Automatically save every change made by you or your team. Revert to previous versions anytime with revision history.',
        },
        {
          title: 'Add Your Own Code',
          text: 'If you need even more flexibility, add custom code to any card.',
        },
        {
          title: 'SEO Tools',
          text: 'Optimize your story for search engines with SEO tools. Enter meta data, titles, and descriptions for improved visibility.',
        },
        {
          title: 'Data Integrations',
          text: 'Integrate external data sources such as APIs or databases to power dynamic and interactive content.',
        },
        {
          title: 'Dynamic Content and Data Binding',
          text: 'Connect text, media and component states to live data, user inputs and URL parameters to create experiences that update dynamically.',
        },
        {
          title: 'Accessibility Features',
          text: 'Include ARIA attributes, screen reader compatibility, and customizable focus indicators to meet accessibility standards.',
        },
        {
          title: 'Multilingual and Localized Versions',
          text: 'Create and manage localized versions while preserving the same approved layouts, components and brand system across markets.',
        },
        {
          title: 'Publish Anywhere',
          text: 'Launch a standalone experience or embed the product inside an existing website or app.',
        },
        {
          title: 'Advanced Conditional Logic',
          text: 'Create logic flows that dynamically show or hide elements, based on user actions or inputs.',
        },
        {
          title: 'Auto Dark Mode Version',
          text: 'Automatically generate a dark mode version of your design, adapting colors, fonts, and backgrounds to suit a dark theme without extra effort.',
        },
        {
          title: 'Custom Dark Mode Version',
          text: 'Gain full control to design a custom dark mode version, allowing you to fine-tune colors, visuals, and styles independently of the light mode.',
        },
        {
          title: 'User Toggle for Dark Mode',
          text: 'Add a floating toggle that lets users switch between light and dark modes.',
        },
        {
          title: 'Dark Mode by URL Parameter',
          text: 'Enable dark mode through a specific URL parameter (e.g., ?darkmode=true), making it easy to share pre-set experiences.',
        },
      ],
    },
    SCEditorCopy: {
      title: 'Let’s meet Storycards Editor',
      embed_1: {
        title: 'video design on on the editor ',
        info: [
          {
            title: 'Design as you want ',
            text: `Absolute control and freedom to design just the way you want it. Start with a template or from scratch and build your custom design. Smooth drag and drop, no coding required. `,
          },
          {
            title: 'Design as you want ',
            text: `Absolute control and freedom to design just the way you want it. Start with a template or from scratch and build your custom design. Smooth drag and drop, no coding required.`,
          },
          {
            title: 'Design as you want ',
            text: `Absolute control and freedom to design just the way you want it. Start with a template or from scratch and build your custom design. Smooth drag and drop, no coding required.`,
          },
          {
            title: 'Design as you want ',
            text: `Absolute control and freedom to design just the way you want it. Start with a template or from scratch and build your custom design. Smooth drag and drop, no coding required.`,
          },
          {
            title: 'Design as you want ',
            text: `Absolute control and freedom to design just the way you want it. Start with a template or from scratch and build your custom design. Smooth drag and drop, no coding required.`,
          },
        ],
      },
    },
    collaboration: {
      title: 'Create and manage Storycards products as a team.',
      pageInfo: {
        title: 'Give each team member the access and tools they need.',
        text: `Create an organization, invite collaborators and see who changed a product and when.\n\nUse groups to separate departments, clients or product portfolios. A person can belong to more than one group when work crosses team boundaries.\n\nSet permissions for analytics, editing, product creation and publishing. Each person sees the products and actions assigned to their role.`,
      },
      infoSection: [
        {
          title: 'Real-time team collaboration',
          text: 'See who is editing a product and review the changes made by each collaborator.',
        },
        {
          title: 'Advanced roles and permissions',
          text: 'Assign separate permissions for editing, analytics, product creation and publishing.',
        },
        {
          title: 'Group-based access control',
          text: 'Divide your organization into groups or departments, allowing teams to focus only on their own products and data. Assign managers to oversee groups and ensure complete separation where needed.',
        },
        {
          title: 'Unlimited revision history',
          text: 'Track every change made to your products. Access a detailed revision history to review or restore previous versions at any time.',
        },
        {
          title: 'Analytics-only access',
          text: 'Grant analytics permissions to data specialists, allowing them to view dashboards and reports without accessing or editing other product details.',
        },
        {
          title: 'Shared product libraries',
          text: 'Create and access a centralized library of templates and reusable products. Save time and maintain quality across all projects.',
        },
        {
          title: 'Integrated workflows across teams',
          text: 'Let marketing, product and data teams work on the same products while keeping responsibilities clear.',
        },
        {
          title: 'Multi-site management',
          text: 'Oversee multiple sites under a single organization. Assign unique permissions and manage content independently for each site.',
        },
        {
          title: 'Force two-factor authentication',
          text: 'Enhance security by requiring two-factor authentication for all team members, ensuring your organization remains protected.',
        },
        {
          title: 'Group segmentation',
          text: 'Easily segment your organization into teams with custom dashboards and separate workflows for each group. Assign users to multiple groups for cross-department collaboration.',
        },
        {
          title: 'Designer and editor workflow',
          text: 'Designers create templates and define protected areas. Content editors update the fields they have permission to change.',
        },
        {
          title: 'Organizational template gallery',
          text: 'Browse through a curated gallery of templates tailored for your organization. Select a template and quickly create new products with just a few clicks, saving time and ensuring consistency.',
        },
        {
          title: 'Virtual collaboration academy',
          text: 'Use virtual training sessions to help teams learn the Storycards workflow and work together consistently.',
        },
        {
          title: 'Priority support for teams',
          text: 'Get email and phone support with the response terms included in your organization’s plan.',
        },
        {
          title: 'Organization-wide analytics',
          text: 'Track performance across your entire organization with unified analytics dashboards. Gain insights into team performance and product success.',
        },
        {
          title: 'Customizable user interfaces',
          text: 'Tailor the Storycards workspace to your team’s specific needs. Simplify workflows and ensure that every user has access to the tools they need.',
        },
        {
          title: 'Integrated data transfer',
          text: 'Ensure compliance and security with real-time data transfers to your CRM or database. Maintain control over your information while simplifying collaboration.',
        },
        {
          title: 'Custom training and onboarding',
          text: 'Schedule personalized virtual training sessions to onboard new team members and help them master Storycards.',
        },
        {
          title: 'Enhanced security for enterprises',
          text: 'Keep your organization secure with enterprise-grade tools such as two-factor authentication, advanced permissions, and user activity tracking.',
        },
        {
          title: 'Fast product search',
          text: 'Search the organization’s library to find products without browsing each team or folder.',
        },
      ],
    },
    dataAndPrivacy: {
      topSection: {
        title: 'Choose where your audience data goes',
        subTitle:
          'Keep data in Storycards or transfer it directly to the CRM, database or server your organization controls.',
      },
      subTitle: 'Collect useful data without losing control of it.',
      subTitleDescription: `Track choices, completion rates and lead submissions by product and card. Export the records you need or send them to your own systems as they are collected.`,
      infoSection: [
        {
          title: 'Data encryption',
          text: `Audience data is encrypted and available only to people with permission to view or export it.`,
        },
        {
          title: 'Export user data',
          text: 'Download leads and responses for a product, or transfer the records to a third-party CRM.',
        },
        {
          title: 'Data integration',
          text: 'Send lead and behavior data to your CRM or data warehouse so it can be used with existing customer records and marketing tools.',
        },
        {
          title: 'Data Bridge',
          text: 'Transfer collected data directly to your servers or databases without storing it in the Storycards cloud.',
        },
        {
          title: 'Data management',
          text: 'Manage product data from the dashboard and delete records when your organization no longer needs them.',
        },
        {
          title: 'Device and platform analysis',
          text: 'Compare behavior on mobile, desktop and tablet to find device-specific differences in completion and engagement.',
        },
        {
          title: 'Geographic performance',
          text: `Track where your audience is located and understand engagement levels by region. Use these insights to tailor your content to specific locations.`,
        },
        {
          title: 'Completion rate analytics',
          text: 'See how many users finish your stories and what might be causing drop-offs. Use this data to refine your storytelling for better engagement.',
        },
        {
          title: 'User journey mapping',
          text: 'Visualize how users navigate through your stories. Identify common paths, bottlenecks, and opportunities for improvement.',
        },
        {
          title: 'Exportable reports',
          text: 'Download analytics reports for review by your team or other stakeholders.',
        },
      ],
    },
    flow: {
      topSection: {
        title: 'Tailor every experience with smart flows',
        subTitle: 'Use answers, scores and known data to change the path, content and outcome.',
      },
      subtitle:
        "Design multiple flows to deliver unique, personalized experiences tailored to each user's journey on every card.",
      description:
        'Storycards lets content editors present different content and card types based on a user’s answers and actions.\n\nThe Smart Flow interface keeps the full journey visible while you create branches, add cards and define what happens next.',
      scrollSection: [
        {
          title: 'Personalize with Dynamic Fields',
          desc: '"Hi, $firstname!" Use Dynamic Content to personalize experiences with details the respondent has provided, such as their name, job or other information gathered in the flow.',
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/get_personal.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/get_personal.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/get_personal.jpg' },
        },
        {
          title: 'Scoring-Driven Navigation',
          desc: 'Use scoring for answers and questions to create personalized user experiences. Tailor results and content based on the points users accumulate, delivering a unique and dynamic journey.',
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/scoring.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/scoring.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/scoring.jpg' },
        },
        {
          title: 'Enhance Journeys with URL Parameters',
          desc: 'Greet users by name right from the start without requiring them to fill in any details. Use URL parameters to pass data like names, IDs, or emails, enabling personalized user experiences that boost conversion rates.',
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/url_parameters.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/url_parameters.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/url_parameters.jpg' },
        },
        {
          title: 'Personalized Flows with Conditional Logic',
          desc: `Create multiple flows and tailor unique experiences based on users' choices. Make your flow smarter by letting it react dynamically to users’ answers.`,
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/flowcondtion.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/flowcondtion.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/flowcondtion.jpg' },
        },
      ],
      scrollSectionTitle: 'Personalize Journeys for Each User',
      scrollSectionSubTitle:
        'Storycards empowers you to craft digital experiences tailored to each user, creating personal journeys that maximize impact and success.',
      infoSection: [
        {
          title: 'Dynamic flow customization',
          text: `Change content and navigation according to the information collected during the journey.`,
        },
        {
          title: 'Smart scoring and tailored outcomes',
          text: `Assign scores to answers and deliver personalized results, adjusting content dynamically based on user performance and preferences.`,
        },
        {
          title: 'Visual conditional logic',
          text: `Design multiple paths that adapt to user choices in real-time, ensuring a custom experience for each participant.`,
        },
        {
          title: 'Real-time personalization with dynamic fields',
          text: `Use user-provided details like names or roles to create engaging, personal experiences, such as, “Hi, $firstname!”`,
        },
        {
          title: 'Personalization with URL parameters',
          text: `Pass user data like names or IDs through URLs to create personalized experiences from the first interaction, boosting engagement and conversion rates.`,
        },
        {
          title: 'Interactive engagement cards',
          text: `Incorporate quizzes, polls, yes/no questions, and lead forms to create a diverse and captivating engagement journey.`,
        },
        {
          title: 'Adaptive user navigation',
          text: `Use answers and scores to dynamically navigate users to relevant content and outcomes, creating meaningful experiences for every participant.`,
        },
        {
          title: 'Repetition control for participation',
          text: `Set your product to allow one-time interactions or unlimited participation, tailoring the experience to your goals.`,
        },
        {
          title: 'Flexible form customization',
          text: `Design forms with fields of your choice, set mandatory fields, and include validations to ensure accurate data collection.`,
        },
        {
          title: 'User-driven result sharing',
          text: `Empower users to share their results directly from your FLOW, boosting virality and driving engagement.`,
        },
        {
          title: 'Unlimited card creation',
          text: `Build an infinite number of cards to design rich, multi-step flows that keep users engaged from start to finish.`,
        },
        {
          title: 'Multi-device optimization',
          text: `Ensure flawless experiences across mobile, tablet, and desktop, adapting every flow to the user’s device for maximum impact.`,
        },
        {
          title: 'A/B testing integration',
          text: `Experiment with different FLOW designs to identify the best-performing structure and optimize user engagement.`,
        },
        {
          title: 'Track and refine user journeys',
          text: `Analyze drop-off points and user interactions to refine your flow, improving engagement and retention.`,
        },
        {
          title: 'Engagement logic insights',
          text: `Use a smart scoring system to uncover patterns in user behavior and adjust your flow for better results.`,
        },
        {
          title: 'Dynamic event tracking',
          text: `Track clicks, form submissions and other selected actions to understand how people use the flow.`,
        },
        {
          title: 'Data-driven optimization tools',
          text: `Leverage real-time analytics to refine and optimize your FLOW, maximizing impact and conversion rates.`,
        },
        {
          title: 'Customized follow-ups',
          text: `Send tailored follow-ups based on user responses and interactions, keeping them engaged even after they leave the FLOW.`,
        },
        {
          title: 'Enhanced user interaction design',
          text: `Combine multiple product types into a single flow, delivering a cohesive and engaging user experience.`,
        },
        {
          title: 'Scoring-based content display',
          text: `Show different cards or content based on accumulated points, creating a dynamic and personalized user journey.`,
        },
      ],
    },
    pricing: {
      title: 'Choose the plan that works for you',
      plans: [
        // {
        // 	name: 'Starter',
        // 	price: '$99',
        // 	billing: 'Billed monthly',
        // 	cta: 'Get Started',
        // 	views: 'Up to 10k page views',
        // 	features: [
        // 		'1 Seat',
        // 		'<p>Create up to 5 stories</p>',
        // 		'Variety of engagement products',
        // 		'Storycards Editor',
        // 		'Design by platform and devices',
        // 		'Build multiple flows',
        // 		'Smart Engagement Logics',
        // 		'Lead generation system',
        // 		'Scoring system',
        // 		'Rich animation',
        // 		'Templates gallery',
        // 		'Real Time Analytics dashboard',
        // 		'Export users data',
        // 		'SEO tools',
        // 		'Embed anywhere',
        // 		'Custom URL',
        // 		'Unlimited revision history',
        // 	],
        // },
        {
          name: 'Professional',
          price: '$499',
          billing: 'Billed monthly',
          cta: 'Get Started',
          views: 'Up to 100K page views',
          features: [
            '10 seats',
            'Create up to 20 stories',
            '<span>Up to 3 teams</span>',
            'Variety of engagement products',
            'Storycards Editor',
            'Design by platform and devices',
            'Build multiple flows',
            'Smart Engagement Logics',
            'Lead generation system',
            'Scoring system',
            'Rich animation',
            'Templates gallery',
            'Real Time Analytics dashboard',
            'Export users data',
            'SEO tools',
            'Embed anywhere',
            'Custom URL',
            'Unlimited revision history',
            'Create your organization',
            'Organization Analytics dashboard',
            'Advanced roles and permissions',
            '24/5 Email Support',
            '99.9% uptime',
          ],
        },
        {
          name: 'Agencies',
          price: '$1999',
          billing: 'Billed monthly',
          cta: 'Get Started',
          views: 'Up to 500K page views',
          features: [
            '30 Seats',
            'Create up to 100 stories',
            '<span>Up to 10 teams</span>',
            'Everything in Professional+',
            'Custom Domain',
            'Custom CSS',
            'Marketing pixels',
            '24/5 Email Support',
            '99.9% uptime',
          ],
        },
        {
          name: 'Enterprise',
          price: 'Custom',
          billing: '',
          cta: 'Contact us',
          views: 'Unlimited page views',
          features: [
            'Unlimited Seats',
            'Unlimited Stories',
            '<span>Unlimited teams</span>',
            'Everything in Agencies+',
            'Custom fonts',
            'Add your own code and scripts',
            'Google Analytics Integration',
            'Add your custom tracker',
            'Dedicated 3rd-party Integration',
            '24/7 email and phone support',
            'Response time SLA',
            'Dedicated account manager',
            'Advanced production services',
            'Virtual training with a professional Storycards designer',
            '99.9% uptime',
          ],
        },
      ],
      warranty: [
        { title: 'Accepted payment methods', description: null },
        {
          title: 'Money back guarantee',
          description: "Try Storycards for 14 days and if you're not 100% satisfied, get your money back.",
        },
        {
          title: 'SSL secure payment',
          description: 'Your information is protected by SSL encryption.',
        },
      ],
    },
    about: {
      topSection: {
        title: 'Meet Our Story',
        subTitle:
          'Storycards is a no-code platform for enterprises that need to create branded interactive products and collect structured first-party data.',
      },
      texts: [
        {
          title: 'Our Journey',
          paragrahs: [
            {
              text: 'Our team began building interactive digital products in 2005 for media companies, telecoms, banks and consumer brands.',
            },
            {
              text: 'Across those projects, organizations kept asking for different experiences built from similar interaction, data and publishing requirements.',
            },
            {
              text: 'In 2018, we began turning that repeated work into an internal platform. It gave clients a faster production process while preserving design, security and data controls.',
            },
            {
              text: 'That internal system became Storycards, a self-service platform for creating and operating interactive products with personalization, analytics and enterprise integrations.',
            },
            {
              text: 'From a pioneering development company in 2005 to a global no-code platform today, our journey has always been driven by one mission: helping organizations turn user engagement into real, measurable business value.',
            },
          ],
        },
        {
          title: 'Our Vision',
          paragrahs: [
            {
              text: 'Our mission is to help enterprises create, operate and improve interactive products in one place.',
            },
            {
              text: 'Teams should be able to launch a useful product and keep improving it after release without rebuilding it from scratch.',
            },
            {
              text: 'Storycards uses engagement, drop-off and behavior data to identify opportunities to improve layout, flow and content.',
            },
            {
              text: 'Teams can compare changes, measure the effect and keep improving a live product over time.',
            },
            {
              text: 'Our vision is a single operating platform where enterprise teams create, analyze and improve personalized digital products.',
            },
          ],
        },
        {
          title: 'What We Do',
          paragrahs: [
            {
              text: 'Storycards is built for interactive marketing, onboarding and data collection products that need more control than a basic site builder or form tool provides.',
            },
            {
              text: 'Start from a prompt, an idea or an approved template. Storycards AI creates an editable draft, and designers can refine the layout, content and interactions in the Studio.',
            },
            {
              text: 'Clicks, answers and choices can be captured as structured data. APIs and integrations send relevant events to the CRM, analytics and marketing systems your organization already uses.',
            },
            {
              text: 'Organizations choose Storycards because it offers the creativity of an agency with the reliability of enterprise software. With features like version control, team roles, and brand libraries, teams can collaborate at scale while maintaining compliance and brand consistency.',
            },
          ],
        },
      ],
      ceo: {
        name: 'Gil Rabbi',
        title: 'Meet the Founder',
        text: "<a href='https://storycards.com/gil-rabbi' target='_blank' id='gilLink'>Gil Rabbi</a> has more than two decades of experience building interactive products for media companies, publishers and other organizations. That work led to Storycards, a no-code platform for creating and operating interactive digital products. <br/><br/> <i>Rabbi was named among Israel’s 40 Under Forty and the Top 100 Most Influential Digital Leaders.</i>",
        description:
          "Rabbi was named in the 40 under Forty list of the most influential people in Israel and as one of 100 most influential people in the Israeli digital world. Rabbi's main specialty over the last decade has been how to make users stay longer on sites and apps, making them more involved in content.",
      },
    },
    pressCenter: {
      title: 'Press Center',
      news: [
        {
          website: 'Authority Magazine',
          title: 'Gil Rabbi Of Storycards On How Their Technological Innovation Will Shake Up The Tech Scene',
          description:
            'As a part of our series about cutting-edge technological breakthroughs, I had the pleasure of interviewing Gil Rabbi.',
          link: 'https://medium.com/authority-magazine/the-future-is-now-gil-rabbi-of-storycards-on-how-their-technological-innovation-will-shake-up-the-39422f9ff920',
          date: '22/12/21',
        },
        {
          website: 'European Business Review',
          title: '5 Innovative Startups To Watch',
          description:
            'In this article, we will present five startups that you should know about. These startups are innovating their industries and are providing lots of value to their partners and clients.',
          link: 'https://www.europeanbusinessreview.com/5-innovative-startups-to-watch/',
          date: '10/01/22',
        },
        {
          website: 'Latinpost',
          title: 'No-Code - Telling The Gil Rabbi Story',
          description:
            'Storycards is a game-changing innovation for eCommerce retailers and for digital agencies. They provide the opportunity to create products without the need for any costly development.',
          link: 'https://www.latinpost.com/articles/153583/20220113/no-code-telling-the-gill-rabbi-story.htm',
          date: '13/01/22',
        },
        {
          website: 'Techbullion',
          title: 'Storycards is Helping Businesses Stay in the Limelight',
          description:
            '“One of the keys to the effectiveness of Storycards is the way that it employs artificial intelligence,” Rabbi explains. “The platform provides feedback on how to improve itself as well as how to increase the level of engagement with the site users. ',
          link: 'https://techbullion.com/in-a-world-where-visual-quality-is-paramount-storycards-is-helping-businesses-stay-in-the-limelight/',
          date: '5/01/22',
        },
        {
          website: 'EuroWeekly',
          title: 'Israeli Tech Mogul Gil Rabbi on How Storycards Engages the Internet',
          description: `These tools put business users in the driver’s seat of custom application development projects with little or no coding skills required.`,
          link: 'https://www.euroweeklynews.com/2021/12/20/israeli-tech-mogul-gil-rabbi-on-how-storycards-engages-the-internet/',
          date: '20/12/21',
        },
        {
          website: 'CTECH',
          title: 'As Google bans third-party cookies, publishers find new ways to gather data',
          description:
            'Storycards is an Israeli company giving power back to publishers who are expecting to lose their advertising methods next year.',
          link: 'https://www.calcalistech.com/ctech/articles/0,7340,L-3927138,00.html',
          date: '13/01/22',
        },
        {
          website: 'itechpost',
          title: "The Future is Here - Gil Rabbi's 'No-Code'",
          description: `Engagement products are a new breed of no-code platforms that focus on enabling marketers to build platforms for customers and prospects-or "customer engagement" if you will.`,
          link: 'http://www.itechpost.com/articles/108686/20220113/the-future-is-here-gil-rabbis-no-code.htm',
          date: '13/01/22',
        },
        {
          website: 'International Business Times UK',
          title: 'Not just Wix: Another Israeli Technology Enables No Coding Engagement Products',
          description:
            'Following the huge success of Wix in no-coding web design, another Israeli company, Storycards, led by engagement guru Gil Rabbi will bring no-coding to online customer engagement.',
          link: 'https://www.ibtimes.co.uk/not-just-wix-another-israeli-technology-enables-no-coding-engagement-products-1695676',
          date: '22/10/21',
        },
        {
          website: 'Entrepreneur',
          title: 'With Most Consumers Online, The No Code Market Is Heating Up',
          description:
            'One such solution is Storycards, an Israeli startup founded by Gil Rabbi. As an enabler of freedom design, Storycards gives users the ability to create any concept and freedom design as they desire.',
          link: 'https://www.entrepreneur.com/article/393233',
          date: '25/10/21',
        },
        {
          website: 'Finance Yahoo',
          title: 'Storycards Has Built a No-Code Platform For Engagement Products',
          description:
            'Storycards has been chosen by The European Business Review as one of 5 innovative startups to watch.',
          link: 'https://finance.yahoo.com/news/storycards-built-no-code-platform-114200621.html',
          date: '12/01/22',
        },
        {
          website: 'World Israel News',
          title: 'How Israeli startups are transforming their industries',
          description:
            'Israel has a flourishing startup ecosystem, which provides a fertile ground for many of the world’s most innovative technologies. It is home to the largest number of startups outside of Silicon Valley and is ranked as one of the most innovative countries in the world.',
          link: 'https://worldisraelnews.com/these-israeli-startups-are-transforming-their-industries/',
          date: '21/12/21',
        },
        {
          website: 'Times of Israel',
          title: 'Tech Leaders: Gil Rabbi, Founder, Storycards',
          description: `Interview is with Gil Rabbi, Founder & CEO of Storycards.`,
          link: 'https://blogs.timesofisrael.com/tech-leaders-gil-rabbi-founder-storycards/',
          date: '12/12/21',
        },
        {
          website: 'Newsroom',
          title: 'Israeli Digital Pioneer Gil Rabbi talks about the inspiration behind Storycards',
          description:
            'It is the Internet’s first no-code engagement product that can change the user experience for people around the globe.',
          link: 'https://newsroompost.com/tech/israeli-digital-pioneer-gil-rabbi-talks-about-the-inspiration-behind-storycards/5049035.html',
          date: '20/12/21',
        },
        {
          website: 'Influencive',
          title: 'MEET GIL RABBI: ONE OF ISRAEL’S DIGITAL PIONEERS',
          description:
            'His passion and astuteness in technology led him to push innovation in the digital world, founding “Storycards” and “Rabbi Interactive Agency.',
          link: 'https://www.influencive.com/meet-gil-rabbi-one-of-israels-digital-pioneers',
          date: '08/02/22',
        },
        {
          website: 'Business News Ledger',
          title: 'The Future Of Engagement Products With Gil Rabbi: No Coding Website Development',
          description:
            'Storycards, a startup based in Israel, aims to revolutionise the way we look at and use no coding website development services.',
          link: 'https://businessnewsledger.com/the-future-of-engagement-products-with-gil-rabbi-no-coding-website-development/',
          date: '06/11/21',
        },
        {
          website: 'Israel National News',
          title: 'The Israeli engagement expert shares his knowledge with everyone, using a new technology',
          description:
            'Israeli engagement expert Gil Rabi announced another new innovation in the area of online engagement.',
          link: 'https://www.israelnationalnews.com/news/316416',
          date: '07/11/21',
        },
        {
          website: 'Apn News',
          title: 'Gil Rabbi’s Storycards garners him headlines across the world',
          description:
            'As the Founder of Storycards and Rabbi Interactive Agency, he has proved what he truly possesses as Israel’s digital pioneer.',
          link: 'https://www.apnnews.com/gil-rabbis-storycards-garners-him-headlines-across-the-world/',
          date: '30/01/22',
        },
        {
          website: 'disruptmagazine',
          title: '“Storycards” by Gil Rabbi, the engagement guru at a global level.',
          description:
            'A quick look around us will let us know how a few people, brands, and businesses have been making noise for the kind of work and the level of contribution they are making in their respective industries.',
          link: 'https://disruptmagazine.com/make-way-for-the-new-no-code-engagement-platform-storycards-by-gil-rabbi-the-engagement-guru-at-a-global-level/',
          date: '30/01/22',
        },
        {
          website: 'News Heads',
          title: `Storycards, a no-code engagement platform by Gil Rabbi`,
          description:
            'Here is a reason he is known as the engagement guru and digital pioneer of Israel, thanks to his tenacity and passion for creating digital products and improving engagement.',
          link: 'https://www.newsheads.in/tech/news/storycards-a-no-code-engagement-platform-by-gil-rabbi-has-thrust-him-forward-in-the-digital-world-article-67099',
          date: '12/01/22',
        },
        {
          website: 'Inventiva',
          title: 'The Engagement Expert Gil Rabbi Reveals What Makes Storycards Unique',
          description:
            'Everyone believes that you need coding knowledge to create a good website that will engage users, however, Israeli tech guru Gil Rabbi has changed these notions.',
          link: 'https://www.inventiva.co.in/trends/inventiva/the-engagement-expert-gil-rabbi-reveals-what-makes-storycards-unique/',
          date: '22/12/21',
        },
        {
          website: 'Newsbreak',
          title: 'Startups To Watch: Simpool, Storycards, SubStrata, AllSeated, Synk',
          description: `The platform’s workflow lets editors develop great interactive experiences, mix and match from various products to create a unique engagement and customize behavior experience according to their needs.
          `,
          link: 'https://original.newsbreak.com/@omri-hurwitz-1589763/2461825986589-startups-to-watch-simpool-storycards-substrata-allseated-synk',
          date: '15/12/21',
        },
        {
          website: 'News Track Live',
          title: 'Tech Superstar Gil Rabbi Of Storycards On How He Is Making The Best Of Artificial Intelligence',
          description:
            'Storycards assign a score for each product created on the platform, which shows the website or app owner what users are doing with it and what should be improved, in real-time.',
          link: 'https://english.newstracklive.com/news/tech-superstar-gil-rabbi-of-storycards-on-how-he-is-making-the-best-of-artificial-intelligence-sc71-nu328-ta328-1205434-1.html',
          date: '15/01/22',
        },
        {
          website: 'Ritz Herald',
          title: 'Gil Rabbi will bring no-coding to the user engagement field',
          description:
            'The huge success of companies that give the option to create websites without knowledge, Storycards, led by one of Israel’s digital pioneers, Gil Rabbi, is making a bold attempt to do the same but in the world of engagement.',
          link: 'https://ritzherald.com/gil-rabbi-will-bring-no-coding-to-the-user-engagement-field/',
          date: '05/11/21',
        },
        {
          website: 'techtribune',
          title:
            'Alors que Google interdit les cookies tiers, les éditeurs trouvent de nouvelles façons decollecter des données',
          description: `C’est un truc de fou, ça va changer tout le secteur publicitaire”, a expliqué Gil Rabbi, fondateur et PDG de Storycards.
          `,
          link: 'https://fr.techtribune.net/google/alors-que-google-interdit-les-cookies-tiers-les-editeurs-trouvent-de-nouvelles-facons-de-collecter-des-donnees/220726/',
          date: '20/01/22',
        },
        {
          website: 'The Science Times',
          title: "The Revolutionary 'No-Code' Era - The Gil Rabbi Story",
          description: `A massive market for lower-tech software is emerging as an increasing number of companies create engagement products powered by a new breed of low-code platforms.`,
          link: 'https://www.sciencetimes.com/articles/35547/20220113/the-revolutionary-no-code-era-the-gil-rabbi-story.htm',
          date: '13/01/22',
        },
        {
          website: 'Entrepreneurs Break',
          title: 'Israeli Tech Mogul Gil Rabbi on How Storycards Engages the Internet',
          description:
            'The company hopes that its invention, the internet’s first no-code engagement product, will deliver next-level engagement and user experience while fostering a new development culture.',
          link: 'https://entrepreneursbreak.com/israeli-tech-mogul-gil-rabbi-on-how-storycards-engages-the-internet.html',
          date: '9/12/21',
        },
        {
          website: 'Digital Journal',
          title: 'Storycards Has Built a No-Code Platform For Engagement Products',
          description:
            'Storycards, the world’s first editor to build professional and custom engagement products in a completely visual canvas with no-code, has been chosen by The European Business Review as one of 5 innovative startups to watch.',
          link: 'https://www.digitaljournal.com/pr/storycards-has-built-a-no-code-platform-for-engagement-products',
          date: '13/01/22',
        },
        {
          website: 'The Daily Scanner',
          title: 'Gil Rabbi: Storycards Is The New Rising Star Platform In The Digital World',
          description:
            'Guru Gil Rabbi is one of the finest coders in the industry, leading to Israeli Storycards, one of the most exciting companies after the success of Wix. The increase of Wix has led to more and more users capable of creating their own websites without any need for knowledge.',
          link: 'https://www.dailyscanner.com/gil-rabbi-storycards-is-the-new-rising-star-platform-the-digital-world/',
          date: '05/11/21',
        },
        {
          website: 'Hudson Weekly',
          title: 'Gil Rabbi Will Bring No-Coding to the User Engagement Field',
          description:
            'With the huge success of companies that give the option to create websites without knowledge, Storycards is making a bold attempt to do the same but in the world of engagement.',
          link: 'https://hudsonweekly.com/gil-rabbi-will-bring-no-coding-to-the-user-engagement-field/',
          date: '05/11/21',
        },
        {
          website: 'Kingstreetoyster',
          title: 'Israeli Tech Mogul Gil Rabbi on How Storycards Engages the Net',
          description:
            'What can make Storycards get the job done with no coding is a nascent technology: AI. Storycards recommended how to increase the level of engagement with the consumers.',
          link: 'https://kingstreetoyster.com/israeli-tech-mogul-gil-rabbi-on-how-storycards-engages-the-net.html',
          date: '09/12/21',
        },

        {
          website: 'Net News Ledger',
          title: 'Gil Rabbi makes Garnering Consumer Attention Easier Than Ever',
          description: `Storycards, an interactive AI digital product aimed at capturing the attention of consumers and increasing interaction, while also providing real-time feedback regarding consumer behavior.`,
          link: 'https://www.netnewsledger.com/2022/02/02/gil-rabbi-makes-garnering-consumer-attention-easier-than-ever/',
          date: '02/02/22',
        },

        {
          website: 'ICE',
          title: `Keshet's partner will sell to others; The channel will separate from the shares and use the product for free`,
          description: `Keshet Broadcasting is cutting back on its holdings in startup Storycards, in light of the founder's expansion plans now turning to the international market.`,
          link: 'https://www.ice.co.il/digital-140/news/article/780989',
          date: '21/06/2020',
        },
      ],
    },
    gilRabbiPage: {
      gilRabbi: 'Gil Rabbi',
      founderAndSeo: 'Founder of Storycards',
      firstTitle:
        'Gil Rabbi is a recognized expert in digital engagement and the founder of Storycards, the AI-powered no-code platform built for enterprises.',
      firstSubtitle:
        'For more than two decades, he has built interactive technology for television, live events and digital products. <br /> <br /> He was twice recognized as one of Israel’s Top 100 Digital Leaders and appeared in the 40 Under Forty list of the country’s most influential business figures.',
      secondTitle:
        'How can technology help people participate and share useful data while still enjoying the experience?',
      secondSubtitle:
        'Gil has spent more than two decades building technology for audience participation. He founded Rabbi Interactive in the early 2000s and led projects for broadcasters, telecom companies and consumer brands. <br/><br/> His team built live voting technology for the <b>Rising Star</b> television format and developed Crowdr, a patented system that synchronized more than 600,000 mobile devices at live events without internet or cellular connectivity. <br/><br/> After repeatedly building similar engagement mechanics for different organizations, the team created an internal platform to handle the common production work. That platform became Storycards. <br/><br/> Gil’s work continues to focus on engagement, personalization and real-time participation.',
    },
    pageNotFound: {
      pageNotFound: 'Page not found',
      backToHome: 'Back to Home page',
    },
    startups: {
      title: 'Storycards for startups and brands',
      subtitle:
        'Generate better quality leads and improve their KPI results faster and focus on marketing strategy without spending money and resources on technology for marketing.',
      items: [
        {
          title: 'Save development resources',
          subtitle: 'Let your teams focus on marketing and free your development team for bigger challenges.',
        },
        {
          title: 'Premium results',
          subtitle:
            'Create better marketing products than you currently produce, get premium results, quicker and without using the development departments.',
        },
        {
          title: 'Stop wasting money on\n AB testing',
          subtitle:
            "AB testing doesn't need to cost a lot of money anymore, creates many marketing products easily, at no cost to market, and learns faster about your target audience.",
        },
        {
          title: 'Increase your leads',
          subtitle: 'Gather more leads for your products with interactive engagement tools.',
        },
        {
          title: 'Personalized segments',
          subtitle: 'Create smarter segments based on users’ choices and run personalized advertising campaigns.',
        },
        {
          title: 'Design by your brand guidelines',
          subtitle:
            ' Customize behavior according to your needs and design according to your brand guidelines so your product will look like it develops special for you.',
        },
        {
          title: 'Analytics and insights',
          subtitle:
            'Storycards analytics dashboard gives you all the information you need but you can always add or connect your tracker.',
        },
        {
          title: 'The data is always yours',
          subtitle: 'Get more value by connecting your existing CRM and marketing tools.',
        },
        {
          title: 'Encrypted data',
          subtitle: 'Your data is encrypted and only the users you have granted permission to access can view it.',
        },
        {
          title: 'Work with your team',
          subtitle: 'Drive collaboration with your team and create groups with advanced roles and permissions.',
        },
      ],
    },
    agencies: {
      title: 'Storycards for digital and advertising agencies',
      subtitle:
        'Make a hundred times more profit once they use Storycards. Bring back the power to the creative people so that they can create their ideas with zero cost.',
      items: [
        {
          title: 'Premium results',
          subtitle:
            'Create better products than you currently do for your clients, get premium results quicker, without the need for developers, and with zero cost.',
        },
        {
          title: 'Create your ideas at zero cost',
          subtitle:
            'Your customers do not have to imagine anymore, create the real product at zero cost and convince your customer to move forward with your idea.',
        },
        {
          title: 'Increase your leads',
          subtitle: 'Gather more leads for your customers with interactive engagement tools.',
        },
        {
          title: 'Personalized segments',
          subtitle: 'Create smarter segments based on users’ choices and run personalized advertising campaigns.',
        },
        {
          title: "Design by your customer's brand guidelines",
          subtitle:
            'Absolute control and freedom to design just the way you want it. Start from scratch and build custom designs for each client by Smooth drag and drop, with no coding.',
        },
        {
          title: 'Analytics and insights',
          subtitle:
            'Storycards analytics dashboard gives you all the information you need but you can always add or connect your tracker.',
        },
        {
          title: 'The data is always yours',
          subtitle: 'Get more value by connecting your existing CRM and marketing tools.',
        },
        {
          title: 'Encrypted data',
          subtitle: 'Your data is encrypted and only the users you have granted permission to access can view it.',
        },
        {
          title: 'Work with your team',
          subtitle: 'Drive collaboration with your team and create groups with advanced roles and permissions.',
        },
        {
          title: 'Publish and Scale',
          subtitle:
            'Go live with one click, and without any worries. Storycards network will scale your product by the number of visitors to your site.',
        },
      ],
    },
    publishers: {
      title: 'Storycards for publishers',
      subtitle:
        'Increase advertising revenue a hundred times more once they use Storycards and let your editors come up with great interactive experiences.',
      items: [
        {
          title: 'Bring the power\n to your editors',
          subtitle:
            'Let your editors come up with great interactive experiences and embed them on articles inside your site and mobile app.',
        },
        {
          title: 'Increase advertising revenue',
          subtitle: 'Sell specific and smarter segments that others cannot offer and increase advertising revenue.',
        },
        {
          title: 'Create premium articles',
          subtitle:
            'Create more interesting premium articles and show news content with interactive tools and at zero cost.',
        },
        {
          title: "Boost your users' engagement",
          subtitle:
            'Give the power to your users and make them involved in your content with polls and voting systems.',
        },
        {
          title: 'An integral part of\n your website',
          subtitle:
            'Storycards product will look like an integral part of the website or mobile app, as though it was custom developed for you.',
        },
        {
          title: 'Design by your site guidelines',
          subtitle:
            'Customize behavior according to your needs and design according to your brand guidelines so your product will look like it develops special for you.',
        },
        {
          title: 'Analytics and insights',
          subtitle:
            'Storycards analytics dashboard gives you all the information you need but you can always add or connect your tracker.',
        },
        {
          title: 'The data is always yours',
          subtitle: 'Get more value by connecting your existing CRM and marketing tools.',
        },
        {
          title: 'Encrypted data',
          subtitle: 'Your data is encrypted and only the users you have granted permission to access can view it.',
        },
        {
          title: 'Work with your team',
          subtitle: 'Drive collaboration with your team and create groups with advanced roles and permissions.',
        },
      ],
    },
    scrollingSection: {
      title: 'Create',
      subtitle:
        'Create premium engaging products with zero development costs. Mix and match from a variety of cards such as quiz, poll, lead form, and more to create a unique engagement experience.',
      items: [
        {
          title: 'Design With Freedom',
          text: 'Create custom responsive layouts on a visual drag-and-drop canvas without writing code.',
        },
        {
          title: 'Publish and Scale',
          text: 'Go live with one click, embed your product in your website or mobile app to increase engagement, or connect your domain for a comprehensive experience in full window mode.',
        },
        {
          title: 'Leads generation and Segments',
          text: 'Increase your leads, learn more about your users, and get real-time user data to find audience and content insights. Create smarter segments based on their choices and run personalized advertising campaigns.',
        },
      ],
    },
    ai: {
      topSection: {
        title: 'Create interactive products with Storycards AI',
        subTitle: `Turn a prompt, URL or topic into an editable Storycards product.`,
        subTitleMob: `Turn a prompt, URL or topic into an editable Storycards product.`,
        btn: 'Start creating',
      },
      sections: {
        sectionTop: {
          title: `<p>Use AI to draft the content, design and logic, then edit every part in the Studio.<p>`,
        },
      },
      scrollSection: [
        {
          title: 'Create a draft from an existing URL',
          desc: 'Storycards AI uses the page’s content and visual language to create a related interactive product.',
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/ai_by_url.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/ai_by_url.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/ai_by_url.jpg' },
        },
        {
          title: 'Turn your prompt into a playful product',
          desc: 'Create customized content for your products automatically by entering your specific topic. Our AI engine automatically writes content, designs images, and creates the game logic.',
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/aiplayful.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/aiplayful.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/aiplayful.jpg' },
        },
        {
          title: 'Gain better performance with Storycards AI',
          desc: `Storycards AI actively monitors user interactions with your products, continuously refining content and design to enhance engagement and conversion rates. This results in strengthened brand loyalty and increased revenue.`,
          videoSrc: {
            desktop:
              'https://player.vimeo.com/progressive_redirect/playback/912106329/rendition/720p/file.mp4?loc=external&log_user=0&signature=854c7a80f1452fe55a6d39e7da9f9dd4d1cbe9e730aac6567ea21c71821c108d',
          },
        },
        {
          title: 'Assign your design to our AI',
          desc: 'Use your website as a design reference so the generated draft follows its colors, type and visual direction.',
          videoSrc: {
            desktop:
              'https://player.vimeo.com/progressive_redirect/playback/912108327/rendition/720p/file.mp4?loc=external&log_user=0&signature=73153747d24f63e610db99527da12dcf02873f37e018e8ab0ec331aec6999214',
          },
        },
      ],
      bottomInfo: {
        paragraph1:
          'We all understand that creating engagement with our audience is the key to enhancing outcomes and data for any organization. However, finding the time to develop such engaging products, brainstorming the necessary content, and designing these items is often a challenge.',
        paragraph2:
          'Storycards can draft trivia, surveys, personality quizzes, games and other interactive products. Your team can then review and edit the result before publishing.',
      },
    },
    homepage: {
      create: {
        eyebrow: 'Build. Measure. Improve.',
        title: 'Every digital product should get better after it launches.',
        subtitle:
          'Create branded interactive experiences as standalone sites or inside your existing websites and apps. Personalize every journey, capture first-party data and continuously improve performance with A/B testing and AI.',
        items: [
          {
            title: 'Find What Works, Faster',
            desc: 'Compare designs, layouts, and flows with smart A/B testing. Let data guide your decisions.',
            videoSrc: {
              desktop: 'https://assts.stories.sc/website/desktop/ab.mp4',
              mobile: 'https://assts.stories.sc/website/mobile/ab.mp4',
            },
            poster: { desktop: 'https://assts.stories.sc/website/poster/ab.jpg' },
          },
          {
            title: 'Real-Time insights and Drop-off',
            desc: 'Track engagement, identify drop-off points, and refine your flow with real-time insights. Analyze performance and optimize for better results.',
            videoSrc: {
              desktop: 'https://assts.stories.sc/website/desktop/insights.mp4',
              mobile: 'https://assts.stories.sc/website/mobile/insights.mp4',
            },
            poster: { desktop: 'https://assts.stories.sc/website/poster/insights.jpg' },
          },
          {
            title: 'Boost Conversions with Storycards AI',
            desc: 'Let Storycards handle A/B testing, data analysis, and flow optimization for you. Deliver tailored, high-performing pages to your visitors faster with AI-driven automation.',
            videoSrc: {
              desktop: 'https://assts.stories.sc/website/desktop/perfromance.mp4',
              mobile: 'https://assts.stories.sc/website/mobile/perfromance.mp4',
            },
            poster: { desktop: 'https://assts.stories.sc/website/poster/perfromance.jpg' },
          },
        ],
      },
      designContent: {
        tabsTitles: ['Design', 'Content'],
        tabsContent: [
          {
            title: 'The professional studio built for designers.',
            text: 'Start with AI, import designs and comments from Figma, or begin with a blank canvas. Perfect every layout and interaction, then turn your design system into reusable rules every team can create within.',
          },
          {
            title: 'Content your way',
            text: 'Let your team focus on content and create impactful products quickly, while preserving your brand’s identity and designer standards.',
          },
        ],
      },
      personalize: {
        title: 'Personalize Journeys for Each User',
        subtitle:
          'Storycards empowers you to craft digital experiences tailored to each user, creating personal journeys that maximize impact and success.',
        items: [
          {
            title: 'Personalize with Dynamic Fields',
            desc: '"Hi, $firstname!" Use Dynamic Content to personalize experiences with details the respondent has provided, such as their name, job or other information gathered in the flow.',
            videoSrc: {
              desktop: 'https://assts.stories.sc/website/desktop/get_personal.mp4',
              mobile: 'https://assts.stories.sc/website/mobile/get_personal.mp4',
            },
            poster: { desktop: 'https://assts.stories.sc/website/poster/get_personal.jpg' },
          },
          {
            title: 'Scoring-Driven Navigation',
            desc: 'Use scoring for answers and questions to create personalized user experiences. Tailor results and content based on the points users accumulate, delivering a unique and dynamic journey.',
            videoSrc: {
              desktop: 'https://assts.stories.sc/website/desktop/scoring.mp4',
              mobile: 'https://assts.stories.sc/website/mobile/scoring.mp4',
            },
            poster: { desktop: 'https://assts.stories.sc/website/poster/scoring.jpg' },
          },
          {
            title: 'Enhance Journeys with URL Parameters',
            desc: 'Greet users by name right from the start without requiring them to fill in any details. Use URL parameters to pass data like names, IDs, or emails, enabling personalized user experiences that boost conversion rates.',
            videoSrc: {
              desktop: 'https://assts.stories.sc/website/desktop/url_parameters.mp4',
              mobile: 'https://assts.stories.sc/website/mobile/url_parameters.mp4',
            },
            poster: { desktop: 'https://assts.stories.sc/website/poster/url_parameters.jpg' },
          },
          {
            title: 'Personalized Flows with Conditional Logic',
            desc: `Create multiple flows and tailor unique experiences based on users' choices. Make your flow smarter by letting it react dynamically to users’ answers.`,
            videoSrc: {
              desktop: 'https://assts.stories.sc/website/desktop/flowcondtion.mp4',
              mobile: 'https://assts.stories.sc/website/mobile/flowcondtion.mp4',
            },
            poster: { desktop: 'https://assts.stories.sc/website/poster/flowcondtion.jpg' },
          },
        ],
      },
      portfolio: {
        title: 'Made in Storycards',
        subtitle:
          'Explore interactive products, stories and data experiences created by brands, publishers and enterprise teams with Storycards.',
      },
      ai: {
        title: 'AI that keeps every product improving.',
        subtitle:
          'Go beyond creation. Turn existing content into structured experiences, learn from real behavior and help teams improve performance over time.',
        items: [
          {
            title: 'Turn existing content into a starting point',
            desc: 'Paste an approved URL and Storycards structures its content into a fully editable experience using your components and brand rules.',
            videoSrc: {
              desktop: 'https://assts.stories.sc/website/desktop/ai_by_url.mp4',
              mobile: 'https://assts.stories.sc/website/mobile/ai_by_url.mp4',
            },
            poster: { desktop: 'https://assts.stories.sc/website/poster/ai_by_url.jpg' },
          },
          {
            title: 'Create inside your approved system',
            desc: 'Describe the audience, goal and data you want to collect. Storycards assembles a production-ready experience your team can review and perfect.',
            videoSrc: {
              desktop: 'https://assts.stories.sc/website/desktop/aiplayful.mp4',
              mobile: 'https://assts.stories.sc/website/mobile/aiplayful.mp4',
            },
            poster: { desktop: 'https://assts.stories.sc/website/poster/aiplayful.jpg' },
          },
          {
            title: 'Learn from real behavior',
            desc: 'Analyze interactions, conversion and flow drop-offs to identify where each journey can improve.',
            videoSrc: {
              desktop:
                'https://player.vimeo.com/progressive_redirect/playback/912106329/rendition/720p/file.mp4?loc=external&log_user=0&signature=854c7a80f1452fe55a6d39e7da9f9dd4d1cbe9e730aac6567ea21c71821c108d',
            },
          },
          {
            title: 'Keep designers in control',
            desc: 'Apply your design system and brand rules to every result, then refine every detail in the professional studio.',
            videoSrc: {
              desktop:
                'https://player.vimeo.com/progressive_redirect/playback/912108327/rendition/720p/file.mp4?loc=external&log_user=0&signature=73153747d24f63e610db99527da12dcf02873f37e018e8ab0ec331aec6999214',
            },
          },
        ],
      },
      noCodeSolutions: {
        title: 'Built for the way enterprises operate.',
        subtitle:
          'Business teams create faster with AI, while design, technology and security teams keep control of brand, access, data and publishing.',
        items: [
          {
            title: 'Build securely with AI, without generating new code.',
            text: 'Storycards AI assembles products from components developed, tested and maintained by Storycards. Every result stays within a controlled, production-ready system.',
          },
          {
            title: 'Your organization’s rules shape every result.',
            text: 'Every prompt is interpreted within your organization’s approved rules for design, brand, language, security and permissions. Teams can create different products without stepping outside the system you control.',
          },
          {
            title: 'Control every team, brand and product',
            text: 'Manage groups, roles, approvals, publishing permissions and SSO across departments, business units, markets and product portfolios. Review changes and restore earlier versions whenever needed.',
          },
          {
            title: 'Designers define the system. Every team creates within it.',
            text: 'Centralize approved components, templates and brand rules. Designers protect layouts, styles and interactions while business and content teams update only what they are allowed to change.',
          },
          {
            title: 'Own your data. Connect your existing stack.',
            text: 'Send structured first-party data directly to the CRM, analytics platform, database or internal systems your organization controls through Data Bridge, APIs and webhooks, without creating another data silo.',
          },
          {
            title: 'Launch anywhere. Operate and improve at scale.',
            text: 'Publish standalone experiences or embed them inside your existing websites and apps. Manage live products centrally, measure behavior, identify drop-offs, run A/B tests and support high-traffic launches.',
          },
        ],
        supportTitle: 'A dedicated team that knows your business.',
        supportText:
          'We work with your teams every day to shape Storycards solutions around real business needs and keep improving them over time.',
      },
      integrationTools: {
        title: 'Connect Storycards to your existing stack.',
        text: `Embed Storycards in your websites and apps. Send structured events and first-party data directly to your analytics, CRM and internal systems through APIs, webhooks, Zapier and Make.`,
      },
    },
    security: {
      topSection: {
        title: 'Security controls for your teams and data',
        subTitle: 'Set access policies, protect accounts and decide how collected data moves through your systems.',
      },
      texts: [
        {
          title: 'Security controls for enterprise products',
          paragrahs: [
            {
              title: 'Managed cloud publishing',
              text: 'Publish Storycards products on managed cloud infrastructure without setting up a separate hosting environment.',
            },
            {
              title: 'Configurable security policies',
              text: 'Every organization can configure its unique security settings, such as password length requirements, two-factor authentication, and role-based access control, ensuring compliance with internal and external regulations.',
            },
            {
              title: 'Roles and permissions',
              text: 'Control who can access specific products, features and data from the organization workspace.',
            },
          ],
        },
        {
          title: 'Data Bridge',
          paragrahs: [
            {
              text: 'Use Data Bridge to transfer collected data directly to your servers or databases without storing it in the Storycards cloud.',
            },
            {
              text: 'Even if you choose to collect and store data on our cloud, you retain full control. You can delete any of your data at any time, giving you the flexibility and assurance to manage your information as you see fit.',
            },
          ],
        },
        {
          title: 'Security for no-code data products',
          paragrahs: [
            {
              title: 'Built for data collection',
              text: 'Unlike traditional NO CODE platforms designed for websites, Storycards specializes in creating data-driven products with embedded tools for data collection and analysis.',
            },
            {
              title: 'Compliance and control',
              text: 'Single sign-on, configurable security policies and API management help organizations apply their own access and compliance requirements.',
            },
            {
              title: 'Scalability',
              text: 'From small-scale deployments to enterprise-grade solutions, our platform adapts to your needs without compromising performance or security.',
            },
          ],
        },
        {
          title: 'How Storycards protects access and data',
          paragrahs: [
            {
              title: 'Encryption standards',
              text: 'All data transfers use HTTPS with TLS 1.2+, and all products include built-in SSL certificates for added security.',
            },
            {
              title: 'Authentication options',
              text: 'We offer advanced login security, including 2-step verification via email, SMS, alongside enterprise-grade SSO.',
            },
            {
              title: 'Threat detection',
              text: 'Our AI-driven threat detection and prevention system monitors potential vulnerabilities, reducing risks and ensuring secure operations.',
            },
          ],
        },
      ],
      iconsBlock: [
        {
          title: 'Always-on DDoS protection',
          text: 'Storycards defends against Distributed Denial of Service (DDoS) attacks of any size, ensuring uninterrupted performance and product availability.',
        },
        {
          title: 'Encryption in transit and at rest',
          text: 'Data in transit uses HTTPS with TLS 1.2+, while data at rest is protected with AES-256 encryption.',
        },
        {
          title: 'Web application firewall',
          text: 'The Web Application Firewall identifies and blocks malicious traffic while allowing regular requests through.',
        },
        {
          title: 'Continuous monitoring',
          text: 'The platform is monitored around the clock so potential threats can be detected and handled quickly.',
        },
      ],
      iconsBlockTitle: 'Advanced Threat Protection',
    },
    api: {
      topSection: {
        title: 'Connect Storycards to your enterprise data',
        subTitle:
          'Send interaction events directly to the systems your organization controls while business teams continue working in a visual, no-code environment.',
      },
      texts: [
        {
          title: 'Why Enterprises Choose the Storycards API',
          paragrahs: [
            {
              title: 'Security First',
              text: 'The client-to-backend architecture can send sensitive user data directly to your organization’s systems so your own privacy and governance rules apply.',
            },
            {
              title: 'Real-Time Data Flow',
              text: 'Capture selected clicks, form submissions, navigation events and performance metrics in real time for use in downstream systems.',
            },
            {
              title: 'Total Flexibility',
              text: 'Choose which events to receive, define the payload format and process them with the tools already used by your technical team.',
            },
          ],
        },
        {
          title: 'Your developers stay in full control while business teams keep enjoying no-code speed.',
          paragrahs: [
            {
              text: 'Connect Storycards with CRM, marketing automation, analytics platforms and internal databases. Events can update systems such as Salesforce, Snowflake or Google Analytics.',
            },
          ],
        },
      ],
      iconsBlock: [
        {
          title: 'Client-to-Backend Model',
          text: 'Events are triggered on the client side and sent straight to your backend.',
        },
        {
          title: 'Event-Based Architecture',
          text: 'Map navigation, form completion and other interactions to events your systems can process.',
        },
        {
          title: 'Your Logic, Your Way',
          text: 'Decide whether incoming data is stored, analyzed, enriched or used to trigger a workflow.',
        },
        {
          title: 'Under your policies',
          text: 'This ensures your data remains in your ecosystem, under your policies, without compromising performance or user experience.',
        },
      ],
      iconsBlockTitle: 'How It Works',
    },
  },
  he: {},
};

type Supportedlanguages = 'en' | 'he';

class Translator {
  private lang: Supportedlanguages;

  constructor(lang: Supportedlanguages) {
    this.lang = lang;
  }

  /**
   * Set traslator language
   * @param lang one of the supported languages
   */
  setLang(lang: Supportedlanguages) {
    this.lang = lang;
  }

  /**
   * Get text by provided path
   * @param path Path to required text, e.g: 'SCEditor.title'
   * @param replace Pattern to replace variables, e.g:
   * - TEXTS: { sample.title: 'Hello {{name}}' };
   * - replace: { name: 'Master' }
   * - result: 'Hello Master' ☠️
   */
  text(path: string, replace: object = {}) {
    let str = _.get(TEXTS, `${this.lang}.${path}`, path);

    _.forEach(replace, (value, key) => {
      str = str.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });

    return str;
  }
}

export default new Translator('en');
